import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import DataTable, { Column } from "../../components/table/dataTable";
import SegmentedControl from "../../components/inputs/segmentedControl";
import Button from "../../components/inputs/button";
import StatusPill from "../../components/misc/statusPill";
import {
	STATUS_TONES,
	campaignLabel,
	formatDateTime,
	formatDay,
	jobDisplayStatus,
	sectionsLabel,
	statusLabel,
} from "./bulkReportJobs";
import { PER_PAGE } from "./useBulkReportJobs";

// ASB-3997: the reports table under the form. Presentational: the rows, the
// paging and the actions come from the page (useBulkReportJobs and the job
// handlers in bulkReportingTool.js).

export const VIEW_OPTIONS = [
	{ label: "My Reports", value: "mine" },
	{ label: "All Reports", value: "all" },
];
export const EMPTY_MINE = "You have no reports from the last 30 days.";
export const EMPTY_ALL = "There are no reports on this ArcTag from the last 30 days.";
export const LOADING_REPORTS = "Loading reports";
export const LINKS_EXPIRE_NOTE = "Links expire 7 days after the report is ready.";

// The pill, with the elapsed time ticking once a second while it generates.
function JobStatus({ job }) {
	const generating = jobDisplayStatus(job) === "generating";
	const [now, setNow] = useState(Date.now());
	useEffect(() => {
		if (!generating) return undefined;
		const id = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(id);
	}, [generating]);
	const status = jobDisplayStatus(job, now);
	return (
		<div className="bulk-report-jobs__status">
			<StatusPill
				tone={STATUS_TONES[status]}
				label={statusLabel(job, now)}
			/>
			{status === "failed" && job.status_message && (
				<p className="bulk-report-jobs__meta">{job.status_message}</p>
			)}
			{status === "ready" && job.expires_at && (
				<p className="bulk-report-jobs__meta">Expires {formatDateTime(job.expires_at)}</p>
			)}
		</div>
	);
}
JobStatus.propTypes = { job: PropTypes.object.isRequired };

function BulkReportJobsTable({
	rows,
	view,
	onViewChange,
	page,
	onPageChange,
	pagination,
	loading,
	loadError,
	onRetry,
	highlightId,
	busyIds,
	canCancel,
	onDownload,
	onGenerateAgain,
	onCancel,
}) {
	const totalItems = pagination?.total_items ?? 0;
	const paged = (pagination?.total_pages ?? 0) > 1;

	const actions = (job) => {
		const status = jobDisplayStatus(job);
		const busy = busyIds.includes(job.id);
		return (
			<div className="bulk-report-jobs__actions">
				{status === "ready" && (
					<Button
						text="Download"
						type="button"
						style="btn-simple"
						size="sm"
						disabled={busy}
						loaded={false}
						onClick={() => onDownload(job)}
					/>
				)}
				{(status === "failed" || status === "expired") && (
					<Button
						text="Generate again"
						type="button"
						style="btn-simple"
						size="sm"
						disabled={false}
						loaded={false}
						onClick={() => onGenerateAgain(job)}
					/>
				)}
				{(status === "queued" || status === "generating") && canCancel(job) && (
					<Button
						text="Cancel"
						type="button"
						style="btn-simple"
						size="sm"
						disabled={busy}
						loaded={false}
						onClick={() => onCancel(job)}
					/>
				)}
			</div>
		);
	};

	const emptyMessage = loading ? LOADING_REPORTS : view === "mine" ? EMPTY_MINE : EMPTY_ALL;

	return (
		<section
			className="bulk-report-jobs"
			aria-labelledby="bulk-report-jobs-title"
		>
			<div className="bulk-report-jobs__head">
				<div>
					<h2
						id="bulk-report-jobs-title"
						className="bulk-report-jobs__title"
					>
						Reports
					</h2>
					<p className="bulk-report-jobs__subtitle">
						Last 30 days · {LINKS_EXPIRE_NOTE}
					</p>
				</div>
				<SegmentedControl
					value={view}
					options={VIEW_OPTIONS}
					optionLabel="label"
					optionValue="value"
					ariaLabel="Which reports"
					onChange={onViewChange}
				/>
			</div>
			{loadError ? (
				// A failed read is an error, not an empty table.
				<div
					className="bulk-report-message bulk-report-message--error bulk-report-jobs__load-error"
					role="alert"
				>
					<p>{loadError}</p>
					<Button
						text="Try again"
						type="button"
						style="btn-simple"
						size="sm"
						disabled={loading}
						loaded={false}
						onClick={onRetry}
					/>
				</div>
			) : (
				<DataTable
					value={rows}
					dataKey="id"
					compact
					emptyMessage={emptyMessage}
					rowClassName={(job) => ({ "bulk-report-jobs__row--highlight": job.id === highlightId })}
					lazy
					paginator={paged}
					first={(page - 1) * PER_PAGE}
					rows={PER_PAGE}
					totalRecords={totalItems}
					onPage={(e) => onPageChange(e.page + 1)}
				>
					<Column
						header="Campaign"
						body={(job) => campaignLabel(job)}
					/>
					<Column
						header="Range"
						bodyClassName="bulk-report-jobs__nowrap"
						body={(job) => `${formatDay(job.spec?.date_start)} to ${formatDay(job.spec?.date_end)}`}
					/>
					<Column
						header="Sections"
						body={(job) => sectionsLabel(job.spec?.sections)}
					/>
					<Column
						header="Requested by"
						body={(job) => job.submitted_by?.name || "—"}
					/>
					<Column
						header="Requested"
						bodyClassName="bulk-report-jobs__nowrap"
						body={(job) => formatDateTime(job.queued_at)}
					/>
					<Column
						header="Status"
						body={(job) => <JobStatus job={job} />}
					/>
					<Column
						header="Actions"
						body={actions}
					/>
				</DataTable>
			)}
		</section>
	);
}

BulkReportJobsTable.propTypes = {
	rows: PropTypes.array.isRequired,
	view: PropTypes.oneOf(["mine", "all"]).isRequired,
	onViewChange: PropTypes.func.isRequired,
	page: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	pagination: PropTypes.object,
	loading: PropTypes.bool.isRequired,
	loadError: PropTypes.string,
	onRetry: PropTypes.func.isRequired,
	highlightId: PropTypes.string,
	busyIds: PropTypes.array.isRequired,
	canCancel: PropTypes.func.isRequired,
	onDownload: PropTypes.func.isRequired,
	onGenerateAgain: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};

export default BulkReportJobsTable;
